import { useTranslation } from '@checkout/core'
import {
  Button,
  Card,
  CheckoutLayout,
  CheckoutHeader,
  CheckoutFooter,
  OrderSummary,
  CheckoutForm,
  LoadingOverlay,
  Modal,
} from '@checkout/ui'
import { useEffect } from 'react'

import styles from './CheckoutPage.module.css'
import { useCheckoutPageController } from '../hooks/useCheckoutPageController'

import type { CheckoutSessionEntry } from '@checkout/core'
import type { BillingInformationValue } from '@checkout/ui'

interface CheckoutPageProps {
  checkoutEntry: CheckoutSessionEntry
  initialBillingInformation?: BillingInformationValue
  onPurchaseSuccessor: (checkoutURL: string, billingInformation: BillingInformationValue) => void
  onReady?: () => void
  onLoadError?: () => void
  enableSuccessorContinuation?: boolean
}

export function CheckoutPage({
  checkoutEntry,
  initialBillingInformation,
  onPurchaseSuccessor,
  onReady,
  onLoadError,
  enableSuccessorContinuation = true,
}: CheckoutPageProps) {
  const { t } = useTranslation()
  const controller = useCheckoutPageController(
    checkoutEntry,
    onPurchaseSuccessor,
    initialBillingInformation,
    enableSuccessorContinuation
  )

  useEffect(() => {
    if (controller.isReady) onReady?.()
  }, [controller.isReady, onReady])

  useEffect(() => {
    if (controller.loadError) onLoadError?.()
  }, [controller.loadError, onLoadError])

  return (
    <CheckoutLayout
      header={
        <CheckoutHeader
          logo={controller.brandConfig.logo.light}
          brandName={controller.brandConfig.name}
          logoWidth={controller.brandConfig.logo.width}
          logoHeight={controller.brandConfig.logo.height}
          showLanguageSwitcher={true}
          languageSwitcherSize={controller.brandConfig.header?.languageSwitcherSize}
          securityBadges={controller.brandConfig.header?.securityBadges}
        />
      }
      sidebar={
        <OrderSummary
          cart={controller.cart}
          isLoading={controller.productLoading}
          error={controller.productError}
          showImages={controller.brandConfig.features.showProductImages}
          formatCurrency={(amount, currencyCode) => controller.formatPrice(amount, currencyCode)}
          title={t('orderSummary.title')}
          showCurrencySelector={controller.currencySelectorEnabled}
          currencies={controller.currencyOptions}
          selectedCurrency={controller.currentCurrency}
          onCurrencyChange={(currencyCode) => void controller.handleCurrencyChange(currencyCode)}
          showCouponInput={controller.couponEnabled}
          showTax={controller.cart.tax > 0}
          couponLinkText={t('orderSummary.couponLink')}
          couponPlaceholder={t('orderSummary.couponPlaceholder')}
          couponApplyText={t('orderSummary.couponApply')}
          onCouponApply={controller.applyCoupon}
          labels={{
            showOrderSummary: t('orderSummary.show'),
            hideOrderSummary: t('orderSummary.hide'),
            subtotal: t('orderSummary.subtotal'),
            discount: t('orderSummary.discount'),
            tax: t('orderSummary.tax'),
            total: t('orderSummary.total'),
          }}
        />
      }
      footer={
        <CheckoutFooter
          disclaimerTitle={controller.disclaimerTitle}
          disclaimer={controller.disclaimer}
          links={controller.footerLinks}
          copyright={controller.copyright}
        />
      }
    >
      <div className={styles.mainContent}>
        <LoadingOverlay
          visible={controller.isPaymentProcessing}
          text={t('callback.paymentPending')}
          subtext={t('callback.pleaseWait')}
        />

        {controller.noPaymentCompletionAvailable && (
          <Card className={styles.formCard}>
            <Button
              size="xl"
              onClick={() => void controller.completeNoPayment()}
              disabled={controller.isSubmitting}
            >
              {t('payment.subscribe')}
            </Button>
          </Card>
        )}

        {!controller.noPaymentCompletionAvailable &&
          (controller.step === 'info' || controller.step === 'payment') && (
            <CheckoutForm
              isLoading={controller.paymentMethodsLoading}
              error={controller.formError}
              accountInfoTitle={t('checkout.section.account')}
              paymentInfoTitle={t('checkout.section.payment')}
              showSectionNumbers={true}
              email={controller.email}
              emailReadOnly={controller.emailReadOnly || controller.customerAbandonment.canRestart}
              onEmailChange={controller.handleEmailChange}
              onEmailBlur={controller.handleEmailBlur}
              emailError={
                controller.emailValidation.state.touched
                  ? controller.emailValidation.state.error
                  : undefined
              }
              emailLabel={t('customer.email')}
              emailPlaceholder={t('customer.emailPlaceholder')}
              paymentMethods={controller.uiPaymentMethods}
              selectedPaymentMethod={controller.selectedPaymentMethod}
              onPaymentMethodSelect={controller.handlePaymentMethodSelect}
              embeddedPaymentSection={controller.embeddedPaymentSection}
              embeddedPaymentSectionVariant={controller.embeddedPaymentSectionVariant}
              billingInformationSection={controller.billingInformationSection}
              payer={controller.payer}
              showEmbeddedPaymentDetailsSection={!!controller.billingInformationSection}
              onSubmit={controller.handleFormSubmit}
              isSubmitting={controller.isSubmitting}
              hideSubmitButton={controller.hideSubmitButton}
              isEmbeddedPaymentValid={controller.isEmbeddedPaymentValid}
              isBillingInformationValid={controller.isBillingInformationValid}
              submitLabel={t(controller.submitLabelKey)}
              termsText={<>{controller.renderTermsText()}</>}
            />
          )}

        {controller.step === 'processing' && (
          <div className={styles.processing}>
            <p>{t('payment.processing')}</p>
          </div>
        )}

        {controller.step === 'attention' && (
          <Card className={styles.formCard}>
            <h2 className={styles.sectionTitle}>{t('modal.paymentTimeout.title')}</h2>
            <p className={styles.paymentError}>{controller.formError}</p>
            <div className={styles.buttonWrapper}>
              <Button
                size="sm"
                onClick={() => void controller.attention.retry()}
                disabled={controller.attention.isRetrying}
              >
                {t('modal.paymentTimeout.tryAgain')}
              </Button>
              {controller.attention.supportHref && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() =>
                    window.open(
                      controller.attention.supportHref ?? undefined,
                      '_blank',
                      'noopener,noreferrer'
                    )
                  }
                >
                  {t('modal.paymentTimeout.contactSupport')}
                </Button>
              )}
            </div>
          </Card>
        )}

        {controller.step === 'complete' && (
          <Card className={styles.formCard}>
            <div className={styles.success}>
              <h2>{t('success.title')}</h2>
              <p>{t('success.message')}</p>
            </div>
          </Card>
        )}

        <Modal
          open={controller.paymentSwitchConfirmation.open}
          onClose={controller.paymentSwitchConfirmation.close}
          title={t(controller.paymentSwitchConfirmation.titleKey)}
          centered
          footer={
            <>
              <Button onClick={controller.paymentSwitchConfirmation.confirm}>
                {t(controller.paymentSwitchConfirmation.confirmKey)}
              </Button>
              <Button variant="ghost" onClick={controller.paymentSwitchConfirmation.close}>
                {t('common.back')}
              </Button>
            </>
          }
        >
          <p>{t(controller.paymentSwitchConfirmation.messageKey)}</p>
        </Modal>

        <Modal
          open={controller.paymentTimeout.open}
          onClose={controller.paymentTimeout.close}
          title={t('modal.paymentTimeout.title')}
          centered
          footer={
            <>
              <Button size="sm" onClick={controller.paymentTimeout.retry}>
                {t('modal.paymentTimeout.tryAgain')}
              </Button>
              {controller.paymentTimeout.supportHref && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    controller.paymentTimeout.close()
                    window.open(
                      controller.paymentTimeout.supportHref ?? undefined,
                      '_blank',
                      'noopener,noreferrer'
                    )
                  }}
                  data-ga-action-name="timeout_contact_support_click"
                  data-ga-module="checkout_form"
                >
                  {t('modal.paymentTimeout.contactSupport')}
                </Button>
              )}
            </>
          }
        >
          <p>{t('modal.paymentTimeout.message')}</p>
        </Modal>
      </div>
    </CheckoutLayout>
  )
}
